// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, Input, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { IAM_ROUTE, GoAction, QueueAlertAction, AlertType, RemoveItem, RemoveType } from '@app/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  DeleteModalComponent,
  RemoveResult
} from '@app/access-management/roles/role-details/delete-modal/delete-modal.component';
import { Store } from '@ngrx/store';
import { State, SelectUsersForRoleAction, LoadingRoleDetailsAction } from '@app/access-management/state';
import { DataTableAjaxComponent, SelectedRows } from '@app/shared/data-table-ajax/data-table-ajax.component';
import { LoggedUserService } from '@app/core/services/logged-user.service';
import { RoleScopesSummary } from '@app/access-management/core/services/role-scopes.helpers';

@Component({
  selector: 'app-role-users-table',
  templateUrl: './role-users-table.component.html',
  styleUrls: ['./role-users-table.component.scss']
})
export class RoleUsersTableComponent implements AfterViewInit {
  @ViewChild('actionTemplate') actionTemplate: any;
  @ViewChild('scopesTpl') scopesTpl: any;
  @ViewChild('nameTemplate') nameTemplate: any;
  @ViewChild('dataTableAjax') dataTableAjax!: DataTableAjaxComponent;

  columns: any[] = [];
  @Input() roleId!: string;
  @Input() displayMode = 'edit';
  @Input() orgId = '';

  _isUserAllowed = true;
  dataTableUrl = '';

  constructor(
    private modalService: NgbModal,
    private loggedUserService: LoggedUserService,
    private store: Store<State>
  ) {}

  ngAfterViewInit() {
    this.dataTableUrl = `/roles/${this.roleId}/users`;
    if (this.orgId !== '') {
      this.dataTableUrl = `/accounts/${this.orgId}/roles/${this.roleId}/users`;
    }
    this.loadUserColumns();
  }

  loadUserColumns() {
    this.columns = [
      {
        name: 'Username',
        prop: 'username',
        flexGrow: 1
      },
      {
        name: 'Scopes',
        flexGrow: 2,
        cellTemplate: this.scopesTpl
      },
      {
        name: ' Organization',
        flexGrow: 1,
        prop: 'account_name'
      },
      {
        name: ' Name',
        flexGrow: 1,
        cellTemplate: this.nameTemplate
      },
      {
        email: 'Email',
        prop: 'email',
        flexGrow: 2
      },
      {
        name: 'Actions',
        prop: 'id',
        cellTemplate: this.actionTemplate,
        sortable: false,
        flexGrow: 1
      }
    ];
    if (this.displayMode === 'view') {
      this.columns.splice(this.columns.length - 1, 1);
    }
  }

  addAccountColumnIfAdmin(condition: boolean) {
    if (condition) {
      const adminColumn = {
        name: 'Organization',
        prop: 'account_name',
        flexGrow: 2
      };

      this.columns.splice(1, 0, adminColumn);
    }
  }

  scopeFormat(userDetails: any[]): string[] {
    return RoleScopesSummary(userDetails);
  }

  onSelectRow(rows: SelectedRows) {
    if (!this._isUserAllowed || this.displayMode === 'view') {
      return;
    }

    this.onEditClick(rows.selected[0], null);
  }

  onRemoveRow(row: any, event: any) {
    if (event) {
      event.stopPropagation();
    }
    const user = row;
    const modalRef = this.modalService.open(DeleteModalComponent, { centered: true, windowClass: 'no-border' });
    modalRef.componentInstance.removeItem = { type: RemoveType.USER, data: user } as RemoveItem;
    // Wait for a result from the modal. Note, this method can't use async await, because it causes funny behavior
    // when called from an angular template
    const resultPromise = modalRef.result as Promise<RemoveResult>;
    resultPromise.then(result => {
      switch (result) {
        case RemoveResult.REMOVE_SUCCEEDED: {
          // Todo: should call update data by calling GET api
          // But here just remove the item from []users at FE
          // Show global indicator info window
          this.dataTableAjax.refreshTable();
          this.store.dispatch(
            new QueueAlertAction({ alertText: `User delete succeeded.`, alertType: AlertType.SUCCESS })
          );
          this.store.dispatch(new LoadingRoleDetailsAction(this.roleId));
          break;
        }

        case RemoveResult.REMOVE_FAILED: {
          this.store.dispatch(new QueueAlertAction({ alertText: `User delete failed.`, alertType: AlertType.ERROR }));
          break;
        }

        default: {
          break;
        }
      }
    });
  }

  onEditClick(row: any, event: any) {
    if (event) {
      event.stopPropagation();
    }

    if (!this._isUserAllowed) {
      return;
    }

    const selectedUser = row;
    this.store.dispatch(new SelectUsersForRoleAction({ roleId: this.roleId, selectedUsers: [selectedUser] }));
    if (this.orgId !== '') {
      this.store.dispatch(
        new GoAction([
          '/',
          IAM_ROUTE,
          'organizations',
          this.orgId,
          'roles',
          this.roleId,
          'users',
          row.username,
          'scopes'
        ])
      );
    } else {
      this.store.dispatch(new GoAction(['/', IAM_ROUTE, 'roles', this.roleId, 'users', row.username, 'scopes']));
    }
  }

  trackColumns(idx: number) {
    return idx;
  }
}



// WEBPACK FOOTER //
// ./src/app/access-management/roles/role-users-table/role-users-table.component.ts